import React from "react";
import { Document, Image, Page, Path, StyleSheet, Svg, Text, View } from "@react-pdf/renderer";
import {
  commonStyles,
  formatDate,
  formatPhoneNumber,
  isValidSummary,
  parseHtmlToText,
  renderBulletPoints,
} from "../Services/ReactPDFService.jsx";

// Executive Sidebar Template - Two-column layout with dark blue sidebar and profile photo
const styles = StyleSheet.create({
  ...commonStyles,
  page: {
    ...commonStyles.page,
    backgroundColor: "#ffffff",
    padding: 0,
    flexDirection: "row",
  },
  // Sidebar (Left Column)
  sidebar: {
    width: "35%",
    backgroundColor: "#2d3e50",
    paddingTop: 30,
    paddingBottom: 30,
    paddingHorizontal: 20,
  },
  // Main Content (Right Column)
  mainContent: {
    width: "65%",
    backgroundColor: "#ffffff",
    paddingTop: 30,
    paddingBottom: 30,
    paddingHorizontal: 25,
  },
  // Profile Photo Section
  profilePhotoContainer: {
    alignItems: "center",
    marginBottom: 20,
  },
  profilePhotoWrapper: {
    width: 100,
    height: 100,
    borderRadius: 50,
    borderWidth: 3,
    borderColor: "#ffffff",
    overflow: "hidden",
    backgroundColor: "#4a5568",
  },
  profilePhoto: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  profilePlaceholder: {
    width: 100,
    height: 100,
    borderRadius: 50,
    borderWidth: 3,
    borderColor: "#ffffff",
    backgroundColor: "#4a5568",
    justifyContent: "center",
    alignItems: "center",
  },
  profileInitials: {
    fontSize: 32,
    fontWeight: 700,
    color: "#ffffff",
  },
  // Sidebar Section Titles
  sidebarSectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: "#ffffff",
    marginBottom: 10,
    paddingBottom: 5,
    borderBottomWidth: 1,
    borderBottomColor: "#4a5c6e",
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  sidebarSectionContainer: {
    marginBottom: 18,
  },
  // Contact Items in Sidebar
  sidebarContactItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 8,
  },
  sidebarContactIcon: {
    width: 12,
    height: 12,
    marginRight: 8,
    marginTop: 1,
  },
  sidebarContactText: {
    fontSize: 9,
    color: "#d1d5db",
    flex: 1,
    lineHeight: 1.4,
  },
  // Education in Sidebar
  sidebarEducationItem: {
    marginBottom: 12,
  },
  sidebarDegree: {
    fontSize: 10,
    fontWeight: 700,
    color: "#ffffff",
    marginBottom: 2,
  },
  sidebarSchool: {
    fontSize: 9,
    color: "#d1d5db",
    marginBottom: 1,
  },
  sidebarEducationDate: {
    fontSize: 8,
    color: "#9ca3af",
  },
  // Skills in Sidebar
  sidebarSkillItem: {
    marginBottom: 6,
  },
  sidebarSkillName: {
    fontSize: 9,
    color: "#d1d5db",
    marginBottom: 3,
  },
  skillProgressBar: {
    height: 4,
    backgroundColor: "#4a5c6e",
    borderRadius: 2,
  },
  skillProgressFill: {
    height: 4,
    backgroundColor: "#60a5fa",
    borderRadius: 2,
  },
  // Languages in Sidebar
  sidebarLanguageItem: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 5,
  },
  sidebarLanguageName: {
    fontSize: 9,
    color: "#d1d5db",
  },
  sidebarLanguageLevel: {
    fontSize: 8,
    color: "#9ca3af",
  },
  // Certifications in Sidebar
  sidebarCertItem: {
    marginBottom: 8,
  },
  sidebarCertName: {
    fontSize: 9,
    color: "#ffffff",
    fontWeight: 600,
  },
  sidebarCertAuthority: {
    fontSize: 8,
    color: "#9ca3af",
  },
  // Main Content - Header
  mainHeaderContainer: {
    marginBottom: 5,
    paddingBottom: 10,
    borderBottomWidth: 2,
    borderBottomColor: "#2d3e50",
  },
  mainNameFirstName: {
    fontSize: 20,
    fontWeight: 700,
    color: "#2d3e50",
    textTransform: "uppercase",
    letterSpacing: 2,
    marginBottom: 15,
  },
  mainNameLastName: {
    fontSize: 20,
    fontWeight: 300,
    color: "#2d3e50",
    textTransform: "uppercase",
    letterSpacing: 1.5,
    marginBottom: 15,
  },
  mainProfessionalTitle: {
    fontSize: 12,
    color: "#6b7280",
    letterSpacing: 1,
  },
  // Main Contact Bar
  mainContactBar: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 15,
    marginTop: 10,
  },
  mainContactItem: {
    flexDirection: "row",
    alignItems: "center",
  },
  mainContactIcon: {
    width: 10,
    height: 10,
    marginRight: 5,
  },
  mainContactText: {
    fontSize: 8,
    color: "#4b5563",
  },
  // Main Section Styling
  mainSectionContainer: {
    marginBottom: 12,
  },
  mainSectionTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: "#2d3e50",
    marginBottom: 8,
    paddingBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  // Summary/About Me
  summaryText: {
    fontSize: 9,
    lineHeight: 1.6,
    color: "#4b5563",
    textAlign: "justify",
  },
  // Experience Items
  experienceItem: {
    marginBottom: 8,
  },
  experienceHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 4,
  },
  experienceLeft: {
    flex: 1,
  },
  experienceRight: {
    alignItems: "flex-end",
  },
  companyName: {
    fontSize: 11,
    fontWeight: 700,
    color: "#1f2937",
  },
  jobTitle: {
    fontSize: 10,
    color: "#6b7280",
    fontStyle: "italic",
  },
  experienceDate: {
    fontSize: 9,
    color: "#6b7280",
  },
  experienceLocation: {
    fontSize: 8,
    color: "#9ca3af",
  },
  bulletPoint: {
    fontSize: 9,
    marginBottom: 2,
    paddingLeft: 10,
    lineHeight: 1.5,
    color: "#4b5563",
  },
  // Projects
  projectItem: {
    marginBottom: 10,
  },
  projectTitle: {
    fontSize: 10,
    fontWeight: 700,
    color: "#1f2937",
    marginBottom: 2,
  },
  projectTech: {
    fontSize: 8,
    color: "#6b7280",
    fontStyle: "italic",
    marginBottom: 3,
  },
  // Achievements
  achievementItem: {
    marginBottom: 4,
  },
  achievementText: {
    fontSize: 9,
    lineHeight: 1.4,
    color: "#4b5563",
  },
  // References
  referencesContainer: {
    flexDirection: "row",
    gap: 20,
  },
  referenceItem: {
    flex: 1,
  },
  referenceName: {
    fontSize: 9,
    fontWeight: 700,
    color: "#1f2937",
    marginBottom: 1,
  },
  referenceTitle: {
    fontSize: 8,
    color: "#6b7280",
    marginBottom: 3,
  },
  referenceContact: {
    flexDirection: "row",
    marginBottom: 2,
  },
  referenceLabel: {
    fontSize: 7,
    fontWeight: 600,
    color: "#4b5563",
    marginRight: 4,
  },
  referenceValue: {
    fontSize: 7,
    color: "#6b7280",
  },
});

// Contact icon paths
const contactIcons = {
  phone: "M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z",
  email: "M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z",
  website: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z",
  location: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
  linkedin: "M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z",
};

const ExecutiveSidebarTemplate = ({ resumeData, customStyles = {}, customSectionOrder = null }) => {
  const {
    personalInfo = {},
    workExperience = [],
    education = [],
    skills = {},
    projects = [],
    certifications = [],
    achievements = [],
    languages = [],
    references = [],
    awards = [],
    volunteering = [],
    interests = [],
    industryExpertise = [],
    publications = [],
    customSections = [],
    sectionTitles = {},
    summary = "",
    professionalTitle = "",
    profilePhoto = {},
  } = resumeData || {};

  // Section titles with custom title support
  const defaultTitles = {
    summary: 'About Me',
    workExperience: 'Experience',
    education: 'Education',
    skills: 'Skills',
    projects: 'Projects',
    certifications: 'Certifications',
    achievements: 'Achievements',
    languages: 'Languages',
    awards: 'Awards',
    volunteering: 'Volunteering',
    interests: 'Interests',
    industryExpertise: 'Industry Expertise',
    publications: 'Publications',
    references: 'References',
  };
  const getSectionTitle = (key) => sectionTitles[key] || defaultTitles[key] || key;

  // Get profile photo URL
  const profilePhotoUrl = profilePhoto?.url || personalInfo?.profilePhotoUrl || null;

  // Get professional title
  const headerTitle = professionalTitle || (workExperience.length > 0 ? workExperience[0].jobTitle : "");

  // Get initials for placeholder
  const getInitials = () => {
    const first = personalInfo.firstName?.charAt(0) || "";
    const last = personalInfo.lastName?.charAt(0) || "";
    return `${first}${last}`.toUpperCase();
  };

  // Create dynamic styles based on customizations
  const createDynamicStyles = (baseStyles, customizations) => {
    const hasCustomizations = customizations && Object.keys(customizations).length > 0;
    if (!hasCustomizations) return baseStyles;

    const { fontSize, sectionSpacing, titleColor, subtitleColor, fontFamily } = customizations;
    const customStyleOverrides = {};

    if (fontFamily) {
      customStyleOverrides.page = { ...baseStyles.page, fontFamily };
    }

    if (fontSize) {
      customStyleOverrides.mainNameFirstName = { ...baseStyles.mainNameFirstName, fontSize: fontSize * 2 };
      customStyleOverrides.mainNameLastName = { ...baseStyles.mainNameLastName, fontSize: fontSize * 2 };
      customStyleOverrides.mainSectionTitle = { ...baseStyles.mainSectionTitle, fontSize: fontSize * 0.85 };
      customStyleOverrides.sidebarSectionTitle = { ...baseStyles.sidebarSectionTitle, fontSize: fontSize * 0.8 };
      customStyleOverrides.companyName = { ...baseStyles.companyName, fontSize: fontSize * 0.8 };
      customStyleOverrides.summaryText = { ...baseStyles.summaryText, fontSize: fontSize * 0.65 };
      customStyleOverrides.bulletPoint = { ...baseStyles.bulletPoint, fontSize: fontSize * 0.65 };
    }

    if (sectionSpacing) {
      customStyleOverrides.mainSectionContainer = { ...baseStyles.mainSectionContainer, marginBottom: sectionSpacing };
      customStyleOverrides.sidebarSectionContainer = { ...baseStyles.sidebarSectionContainer, marginBottom: sectionSpacing };
    }

    if (titleColor) {
      customStyleOverrides.mainNameFirstName = {
        ...(customStyleOverrides.mainNameFirstName || baseStyles.mainNameFirstName),
        color: titleColor,
      };
      customStyleOverrides.mainNameLastName = {
        ...(customStyleOverrides.mainNameLastName || baseStyles.mainNameLastName),
        color: titleColor,
      };
      customStyleOverrides.mainSectionTitle = {
        ...(customStyleOverrides.mainSectionTitle || baseStyles.mainSectionTitle),
        color: titleColor,
        borderBottomColor: titleColor,
      };
      customStyleOverrides.mainHeaderContainer = {
        ...baseStyles.mainHeaderContainer,
        borderBottomColor: titleColor,
      };
      customStyleOverrides.sidebar = {
        ...baseStyles.sidebar,
        backgroundColor: titleColor,
      };
    }

    if (subtitleColor) {
      customStyleOverrides.mainProfessionalTitle = { ...baseStyles.mainProfessionalTitle, color: subtitleColor };
      customStyleOverrides.experienceDate = { ...baseStyles.experienceDate, color: subtitleColor };
    }

    return { ...baseStyles, ...customStyleOverrides };
  };

  const mergedStyles = createDynamicStyles(styles, customStyles);

  // Combine all skills
  const allSkills = (() => {
    if (Array.isArray(skills)) return skills;
    if (skills.list && Array.isArray(skills.list) && skills.list.length > 0) return skills.list;
    const combinedSkills = [
      ...(skills.technical || []),
      ...(skills.soft || []),
      ...(skills.tools || []),
      ...(skills.other || []),
    ];
    if (combinedSkills.length > 0) return combinedSkills;
    if (skills.categorized) {
      return [
        ...(skills.categorized.technical || []),
        ...(skills.categorized.soft || []),
        ...(skills.categorized.tools || []),
        ...(skills.categorized.other || []),
      ];
    }
    return [];
  })();

  const getSkillName = (skill) => {
    if (typeof skill === "string") return skill;
    if (skill && typeof skill === "object") return skill.name || skill.skill || "";
    return "";
  };

  const getSkillLevel = (skill) => {
    if (typeof skill === "object" && skill.level) {
      const levelMap = { beginner: 25, intermediate: 50, advanced: 75, expert: 100 };
      return levelMap[skill.level.toLowerCase()] || 70;
    }
    return 70; // Default level
  };

  // Helper to get language name from string or object
  const getLanguageName = (lang) => {
    if (typeof lang === "string") return lang;
    if (lang && typeof lang === "object") return lang.name || lang.language || "";
    return "";
  };

  // Helper to get interest/expertise name from string or object
  const getItemName = (item) => {
    if (typeof item === "string") return item;
    if (item && typeof item === "object") return item.name || item.title || "";
    return "";
  };

  // Render contact icon
  const renderContactIcon = (iconPath, color = "#9ca3af") => (
    <Svg style={mergedStyles.sidebarContactIcon} viewBox="0 0 24 24">
      <Path d={iconPath} fill={color} />
    </Svg>
  );

  const renderMainContactIcon = (iconPath) => (
    <Svg style={mergedStyles.mainContactIcon} viewBox="0 0 24 24">
      <Path d={iconPath} fill="#6b7280" />
    </Svg>
  );

  return (
    <Document>
      <Page size="A4" style={mergedStyles.page} wrap={true}>
        {/* SIDEBAR - Left Column */}
        <View style={mergedStyles.sidebar}>
          {/* Profile Photo */}
          <View style={mergedStyles.profilePhotoContainer}>
            {profilePhotoUrl ? (
              <View style={mergedStyles.profilePhotoWrapper}>
                <Image src={profilePhotoUrl} style={mergedStyles.profilePhoto} />
              </View>
            ) : (
              <View style={mergedStyles.profilePlaceholder}>
                <Text style={mergedStyles.profileInitials}>{getInitials()}</Text>
              </View>
            )}
          </View>

          {/* Contact Info in Sidebar */}
          <View style={mergedStyles.sidebarSectionContainer}>
            <Text style={mergedStyles.sidebarSectionTitle}>Contact</Text>
            {personalInfo.phone && (
              <View style={mergedStyles.sidebarContactItem}>
                {renderContactIcon(contactIcons.phone)}
                <Text style={mergedStyles.sidebarContactText}>{formatPhoneNumber(personalInfo.phone)}</Text>
              </View>
            )}
            {personalInfo.email && (
              <View style={mergedStyles.sidebarContactItem}>
                {renderContactIcon(contactIcons.email)}
                <Text style={mergedStyles.sidebarContactText}>{personalInfo.email}</Text>
              </View>
            )}
            {personalInfo.location && (
              <View style={mergedStyles.sidebarContactItem}>
                {renderContactIcon(contactIcons.location)}
                <Text style={mergedStyles.sidebarContactText}>{personalInfo.location}</Text>
              </View>
            )}
            {personalInfo.website && (
              <View style={mergedStyles.sidebarContactItem}>
                {renderContactIcon(contactIcons.website)}
                <Text style={mergedStyles.sidebarContactText}>{personalInfo.website}</Text>
              </View>
            )}
            {personalInfo.linkedin && (
              <View style={mergedStyles.sidebarContactItem}>
                {renderContactIcon(contactIcons.linkedin)}
                <Text style={mergedStyles.sidebarContactText}>{personalInfo.linkedin}</Text>
              </View>
            )}
          </View>

          {/* Education in Sidebar */}
          {education.length > 0 && (
            <View style={mergedStyles.sidebarSectionContainer}>
              <Text style={mergedStyles.sidebarSectionTitle}>Education</Text>
              {education.map((edu, index) => (
                <View key={index} style={mergedStyles.sidebarEducationItem}>
                  <Text style={mergedStyles.sidebarDegree}>
                    {edu.degree}{edu.fieldOfStudy ? ` in ${edu.fieldOfStudy}` : ""}
                  </Text>
                  <Text style={mergedStyles.sidebarSchool}>{edu.school}</Text>
                  <Text style={mergedStyles.sidebarEducationDate}>
                    {formatDate(edu.startDate)} – {edu.endDate === "Present" ? "Present" : formatDate(edu.endDate)}
                  </Text>
                </View>
              ))}
            </View>
          )}

          {/* Skills with Progress Bars in Sidebar */}
          {allSkills.length > 0 && (
            <View style={mergedStyles.sidebarSectionContainer}>
              <Text style={mergedStyles.sidebarSectionTitle}>Skills</Text>
              {allSkills.slice(0, 8).map((skill, index) => (
                <View key={index} style={mergedStyles.sidebarSkillItem}>
                  <Text style={mergedStyles.sidebarSkillName}>{getSkillName(skill)}</Text>
                  <View style={mergedStyles.skillProgressBar}>
                    <View style={[mergedStyles.skillProgressFill, { width: `${getSkillLevel(skill)}%` }]} />
                  </View>
                </View>
              ))}
            </View>
          )}

          {/* Languages in Sidebar */}
          {languages && languages.length > 0 && (
            <View style={mergedStyles.sidebarSectionContainer}>
              <Text style={mergedStyles.sidebarSectionTitle}>Languages</Text>
              {languages.map((lang, index) => (
                <View key={index} style={mergedStyles.sidebarLanguageItem}>
                  <Text style={mergedStyles.sidebarLanguageName}>{getLanguageName(lang)}</Text>
                  {lang.proficiency && <Text style={mergedStyles.sidebarLanguageLevel}>{lang.proficiency}</Text>}
                </View>
              ))}
            </View>
          )}

          {/* Certifications in Sidebar */}
          {certifications.length > 0 && (
            <View style={mergedStyles.sidebarSectionContainer}>
              <Text style={mergedStyles.sidebarSectionTitle}>Certifications</Text>
              {certifications.slice(0, 4).map((cert, index) => (
                <View key={index} style={mergedStyles.sidebarCertItem}>
                  <Text style={mergedStyles.sidebarCertName}>{cert.name}</Text>
                  <Text style={mergedStyles.sidebarCertAuthority}>
                    {cert.authority || cert.issuer}{cert.date && ` • ${formatDate(cert.date)}`}
                  </Text>
                </View>
              ))}
            </View>
          )}
        </View>

        {/* MAIN CONTENT - Right Column */}
        <View style={mergedStyles.mainContent}>
          {/* Header with Name */}
          <View style={mergedStyles.mainHeaderContainer}>
            <Text style={mergedStyles.mainNameFirstName}>{personalInfo.firstName}</Text>
            <Text style={mergedStyles.mainNameLastName}>{personalInfo.lastName}</Text>
            {headerTitle && <Text style={mergedStyles.mainProfessionalTitle}>{headerTitle}</Text>}
          </View>

          {/* About Me / Summary */}
          {summary && isValidSummary(summary) && (
            <View style={mergedStyles.mainSectionContainer}>
              <Text style={mergedStyles.mainSectionTitle}>About Me</Text>
              <Text style={mergedStyles.summaryText}>{parseHtmlToText(summary)}</Text>
            </View>
          )}

          {/* Work Experience */}
          {workExperience.length > 0 && (
            <View style={mergedStyles.mainSectionContainer}>
              <Text style={mergedStyles.mainSectionTitle}>Experience</Text>
              {workExperience.map((job, index) => (
                <View key={index} style={mergedStyles.experienceItem}>
                  <View style={mergedStyles.experienceHeader}>
                    <View style={mergedStyles.experienceLeft}>
                      <Text style={mergedStyles.companyName}>{job.company}</Text>
                      {job.jobTitle && <Text style={mergedStyles.jobTitle}>{job.jobTitle}</Text>}
                    </View>
                    <View style={mergedStyles.experienceRight}>
                      <Text style={mergedStyles.experienceDate}>
                        {formatDate(job.startDate)} – {" "}
                        {job.isCurrentJob || job.endDate === "Present" || job.endDate === "Current" || !job.endDate
                          ? "Present"
                          : formatDate(job.endDate)}
                      </Text>
                      {job.location && <Text style={mergedStyles.experienceLocation}>{job.location}</Text>}
                    </View>
                  </View>
                  {job.description && renderBulletPoints(parseHtmlToText(job.description), mergedStyles)}
                </View>
              ))}
            </View>
          )}

          {/* Projects */}
          {projects.length > 0 && (
            <View style={mergedStyles.mainSectionContainer}>
              <Text style={mergedStyles.mainSectionTitle}>Projects</Text>
              {projects.map((project, index) => (
                <View key={index} style={mergedStyles.projectItem}>
                  <Text style={mergedStyles.projectTitle}>{project.name}</Text>
                  {project.technologies && (
                    <Text style={mergedStyles.projectTech}>Technologies: {project.technologies}</Text>
                  )}
                  {project.description && (
                    <View style={{ marginTop: 3 }}>
                      {renderBulletPoints(parseHtmlToText(project.description), mergedStyles)}
                    </View>
                  )}
                </View>
              ))}
            </View>
          )}

          {/* Achievements */}
          {achievements.length > 0 && (
            <View style={mergedStyles.mainSectionContainer}>
              <Text style={mergedStyles.mainSectionTitle}>Achievements</Text>
              {achievements.map((achievement, index) => (
                <View key={index} style={mergedStyles.achievementItem}>
                  {achievement.description && (
                    <Text style={mergedStyles.achievementText}>• {parseHtmlToText(achievement.description)}</Text>
                  )}
                </View>
              ))}
            </View>
          )}

          {/* References */}
          {references && references.length > 0 && (
            <View style={mergedStyles.mainSectionContainer} wrap={false}>
              <Text style={mergedStyles.mainSectionTitle}>{getSectionTitle('references')}</Text>
              <View style={mergedStyles.referencesContainer}>
                {references.slice(0, 2).map((ref, index) => (
                  <View key={index} style={mergedStyles.referenceItem}>
                    <Text style={mergedStyles.referenceName}>{ref.name}</Text>
                    <Text style={mergedStyles.referenceTitle}>{ref.title || ref.position}</Text>
                    {ref.phone && (
                      <View style={mergedStyles.referenceContact}>
                        <Text style={mergedStyles.referenceLabel}>Phone:</Text>
                        <Text style={mergedStyles.referenceValue}>{ref.phone}</Text>
                      </View>
                    )}
                    {ref.email && (
                      <View style={mergedStyles.referenceContact}>
                        <Text style={mergedStyles.referenceLabel}>Email:</Text>
                        <Text style={mergedStyles.referenceValue}>{ref.email}</Text>
                      </View>
                    )}
                  </View>
                ))}
              </View>
            </View>
          )}

          {/* Awards */}
          {awards && awards.length > 0 && (
            <View style={mergedStyles.mainSectionContainer}>
              <Text style={mergedStyles.mainSectionTitle}>{getSectionTitle('awards')}</Text>
              {awards.map((award, index) => (
                <View key={index} style={mergedStyles.achievementItem}>
                  <Text style={mergedStyles.companyName}>{award.title || award.name}</Text>
                  {award.issuer && <Text style={mergedStyles.jobTitle}>{award.issuer}</Text>}
                  {award.date && <Text style={mergedStyles.experienceDate}>{formatDate(award.date)}</Text>}
                  {award.description && (
                    <Text style={mergedStyles.achievementText}>{parseHtmlToText(award.description)}</Text>
                  )}
                </View>
              ))}
            </View>
          )}

          {/* Volunteering */}
          {volunteering && volunteering.length > 0 && (
            <View style={mergedStyles.mainSectionContainer}>
              <Text style={mergedStyles.mainSectionTitle}>{getSectionTitle('volunteering')}</Text>
              {volunteering.map((vol, index) => (
                <View key={index} style={mergedStyles.experienceItem}>
                  <View style={mergedStyles.experienceHeader}>
                    <View style={mergedStyles.experienceLeft}>
                      <Text style={mergedStyles.companyName}>{vol.organization}</Text>
                      {(vol.role || vol.position) && <Text style={mergedStyles.jobTitle}>{vol.role || vol.position}</Text>}
                    </View>
                    <View style={mergedStyles.experienceRight}>
                      {(vol.startDate || vol.endDate) && (
                        <Text style={mergedStyles.experienceDate}>
                          {formatDate(vol.startDate)} – {vol.endDate === "Present" ? "Present" : formatDate(vol.endDate)}
                        </Text>
                      )}
                    </View>
                  </View>
                  {vol.description && renderBulletPoints(parseHtmlToText(vol.description), mergedStyles)}
                </View>
              ))}
            </View>
          )}

          {/* Interests */}
          {interests && interests.length > 0 && (
            <View style={mergedStyles.mainSectionContainer}>
              <Text style={mergedStyles.mainSectionTitle}>{getSectionTitle('interests')}</Text>
              <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
                {interests.map((interest, index) => (
                  <Text key={index} style={mergedStyles.achievementText}>
                    • {getItemName(interest)}
                  </Text>
                ))}
              </View>
            </View>
          )}

          {/* Industry Expertise */}
          {industryExpertise && industryExpertise.length > 0 && (
            <View style={mergedStyles.mainSectionContainer}>
              <Text style={mergedStyles.mainSectionTitle}>{getSectionTitle('industryExpertise')}</Text>
              <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
                {industryExpertise.map((expertise, index) => (
                  <Text key={index} style={mergedStyles.achievementText}>
                    • {getItemName(expertise)}
                  </Text>
                ))}
              </View>
            </View>
          )}

          {/* Publications */}
          {publications && publications.length > 0 && (
            <View style={mergedStyles.mainSectionContainer}>
              <Text style={mergedStyles.mainSectionTitle}>{getSectionTitle('publications')}</Text>
              {publications.map((pub, index) => (
                <View key={index} style={mergedStyles.projectItem}>
                  <Text style={mergedStyles.projectTitle}>{pub.title}</Text>
                  {pub.publisher && <Text style={mergedStyles.projectTech}>{pub.publisher}</Text>}
                  {pub.date && <Text style={mergedStyles.projectTech}>{formatDate(pub.date)}</Text>}
                  {pub.description && (
                    <Text style={mergedStyles.achievementText}>{parseHtmlToText(pub.description)}</Text>
                  )}
                </View>
              ))}
            </View>
          )}

          {/* Custom Sections */}
          {customSections && customSections.length > 0 && customSections.map((customSection) => {
            if (!customSection.id || !customSection.title || !customSection.items?.length) return null;
            return (
              <View key={customSection.id} style={mergedStyles.mainSectionContainer}>
                <Text style={mergedStyles.mainSectionTitle}>{customSection.title}</Text>
                {customSection.items.map((item, itemIndex) => (
                  <View key={itemIndex} style={mergedStyles.achievementItem}>
                    {item.content && (
                      <Text style={mergedStyles.achievementText}>{parseHtmlToText(item.content)}</Text>
                    )}
                  </View>
                ))}
              </View>
            );
          })}
        </View>
      </Page>
    </Document>
  );
};

export default ExecutiveSidebarTemplate;
